<template>
  <b-modal
    id="form-import-modal"
    size="xl"
    no-close-on-backdrop
    no-enforce-focus
    no-close-on-esc
    :ok-title="trans('import')"
    :ok-disabled="!canImport"
    :cancel-title="trans('cancel')"
    :title="trans('import')"
    @shown="onShown"
    @hidden="onHidden"
    @ok="onImport"
  >
    <b-row v-if="!!recipient_form">
      <b-col>
        <p>
          {{ trans('form_import_instructions_1') }}
          <br/>
          <b-button variant="outline-primary" size="sm" class="py-0" :href="schemaUrl" target="_blank">
            <i class="fa fa-fw fa-file-excel"></i>
            {{ trans('sample') }}
          </b-button>
        </p>
        <p>
          {{ trans('form_import_instructions_2') }}
          <b-form-group>
            <b-input-group>
              <b-form-input
                ref="input"
                size="sm"
                autocomplete="off"
                :placeholder="trans('data')"
                :disabled="busy"
                @paste="onPaste"
              />
              <b-input-group-append v-if="!!text">
                <b-button variant="outline-danger" size="sm" @click="reset">
                  <i class="fa fa-fw fa-times"></i>
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </b-form-group>
        </p>
      </b-col>
      <b-col>
        <p>{{ trans('form_import_instructions_3') }}</p>
        <p>
          {{ trans('form_import_instructions_4') }}
          <small v-if="!showJdmConsent" class="d-block ml-3 text-muted">
            <em>{{ trans('form_import_instructions_note') }}</em>
          </small>
          <b-alert variant="primary" :show="showJdmConsent" class="p-2 mx-3">
            <small>
              <i class="fa fa-fw fa-envelope"></i>
              {{ trans('jdm_form_import_instructions_note') }}
            </small>
            <b-button variant="link" size="sm" v-b-modal.jdm-consent-modal>
              <i class="fa fa-fw fa-info-circle"></i>
              {{ trans('information') }}
            </b-button>
          </b-alert>
        </p>
      </b-col>
    </b-row>

    <p v-if="!!errors">
      <small class="d-block text-danger" v-for="(e, index) in errors" :key="'error'+index">
        {{ e }}
      </small>
    </p>
    <p v-if="canImport">
      <small class="text-success">
        {{ trans('data_valid_can_import').replace(':rows', this.items.length) }}
      </small>
    </p>

    <b-table
      id="form-import-table"
      ref="table"
      class="bg-white"
      responsive
      bordered
      small
      show-empty
      :per-page="perPage"
      :current-page="currentPage"
      :fields="fields"
      :items="items"
    >
      <template v-slot:empty>
        <p align="center">
          <span v-if="!busy">{{ trans('none') }}</span>
          <span v-if="busy">
            <i class="fa fa-fw fa-circle-notch fa-spin" v-if="busy"></i>
            {{ trans('please_wait') }}
          </span>
        </p>
      </template>
      <template #head()="data">
        <small class="font-weight-bold" :title="data.column">{{ data.label }}</small>
      </template>
      <template #cell(row)="data">
        <small class="text-muted">{{ data.value + 1 }}</small>
      </template>
      <template #cell()="data">
        <small :class="error_r == data.item.row && error_c == data.field.key ? 'text-danger' : ''">{{ data.value }}</small>
      </template>
    </b-table>
    <b-row>
      <b-col class="col-8">
        <b-pagination
          :total-rows="!!items ? items.length : 0"
          :per-page="perPage"
          v-model="currentPage"
          aria-controls="form-import-table"
          align="center"
          size="sm"
        ></b-pagination>
      </b-col>
      <b-col class="col-4">
        <b-form-group class="w-50">
          <b-form-select v-model="perPage" size="sm" :options="perPageOptions" :disabled="busy" />
        </b-form-group>
      </b-col>
    </b-row>

    <b-form-group v-if="isPaymentForm" :label="trans('import_payment_form_instructions')">
      <b-form-radio-group
        v-model="pay_with"
        stacked
        :options="payWithOptions"
        :disabled="busy"
      ></b-form-radio-group>
    </b-form-group>

    <p v-if="busy">
      <i class="fa fa-fw fa-circle-notch fa-spin"></i>
      <span v-if="operation_index !== null">{{ trans(operation == 'validate' ? 'validating' : 'importing') }} {{ operation_index+1 }}/{{ items.length }}.</span>
      {{ trans('please_wait') }}
    </p>
    <p v-if="!!error" class="text-danger">
      {{ error }}
    </p>
    <p v-if="!!error && operation_index !== null" class="text-danger">
      {{ trans('import_correct_errors').replace(':row', operation_index+4) }}
    </p>
  </b-modal>
</template>

<script>
  import csv2json from 'csvjson-csv2json/csv2json'

  function parseCsv(csv) {
    let data = csv2json(csv)
    return data
  }



  function buildParticipantFromSchema(sch, item) {
    return _.reduce(sch.columns, (person, col) => {
      if (_.allPersonAttributes.indexOf(col.attribute) === -1) return person
      let name = col.name
      let key = name.replace(new RegExp(`^${sch.slug}\.`), '')
      person[key] = item[name]
      if (key == 'gender') person[key] = _.serializeGender(item[name])
      if (key == 'relationship') person[key] = _.serializeRelationship(item[name])
      if (key == 'medical_conditions') person[key] = _.serializeMedicalConditions(item[name])
      if (key == 'allergies') person[key] = _.serializeAllergies(item[name])
      return person
    }, {})
  }

  function buildContactsFromSchema(sch, item) {
    // We assume the column name is in this form: {slug}.{index}.{attr}
    // For example: contacts0.0.first_name
    let persons = _.reduce(sch.columns, (persons, col) => {
      if (_.allPersonAttributes.indexOf(col.attribute) === -1) return person
      let name = col.name
      let parts = name.split('.')
      let index = parseInt(parts[1], 10)
      let key = parts[2]
      let person = persons[index] || {}
      persons[index] = person
      person[key] = item[name]
      if (key == 'gender') person[key] = _.serializeGender(item[name])
      if (key == 'relationship') person[key] = _.serializeRelationship(item[name])
      if (key == 'medical_conditions') person[key] = _.serializeMedicalConditions(item[name])
      if (key == 'allergies') person[key] = _.serializeAllergies(item[name])
      return persons
    }, [])

    // Remove ones that are completely empty
    return _.reduce(persons, (persons, p) => {
      let is_empty = _.reduce(_.keys(p), (is_empty, key) => {
        if (!!p[key]) is_empty = false
        return is_empty
      }, true)
      if (!is_empty) persons.push(p)
      return persons
    }, [])
  }

  // IMPORTANT NOTE: This component is used both in the Client Account and in the Form Respondents table on the admin side.
  // Make sure it works in both places!
  export default {
    props: {
      organization: Object,
      recipient_form: Object,
      // Only pass the following when in Client account. Omit on the admin side.
      client: Object,
      clientTab: Object, // For JDM, will be {id: 'jdm'}
      showJdmConsent: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        trans: trans,
        moment: moment,
        busy: false,
        error: null,
        tab: 0,
        schema: null,
        text: null,
        headers: null,
        header_row: null,
        header_r: null,
        errors: [],
        error_row: null,
        error_r: null,
        error_schema: null,
        error_c: null,
        is_valid: false,
        items: null,
        perPage: 5,
        currentPage: 1,
        operation: null,
        operation_index: null,
        pay_with: 'pay_later',
        conditionals: {},
      }
    },
    computed: {
      isPaymentForm() {
        return !!this.recipient_form && this.recipient_form.type == 'payment_form'
      },
      hasSchedulePaymentsWithStripe() {
        return !!this.recipient_form &&
          !!this.recipient_form.payment_options &&
          !!this.recipient_form.payment_options.schedule_payments_options &&
          this.recipient_form.payment_options.schedule_payments_options.enabled
      },
      payWithOptions() {
        return _.compact([
          {value: 'pay_later', text: trans('pay_later')},
          {value: 'balance_other_platform', text: `${trans('mark_paid')}: ${trans('balance_other_platform').toLowerCase()}`},
          this.hasSchedulePaymentsWithStripe ? {value: 'stripe_scheduled', text: trans('schedule_payments')} : null,
        ])
      },
      schemaUrl() {
        if (!!this.client && !!this.clientTab) {
          return `/org/${this.organization.id}/client/tab/${this.clientTab.id}/form/${this.recipient_form.id}/schema`
        }
        return `/organization/${this.organization.id}/form/${this.recipient_form.id}/schema`
      },
      fields() {
        return _.reduce(this.schema || [], (fields, sch) => {
          return _.reduce(sch.columns, (fields, column) => {
            fields.push({
              key: column.name,
              label: column.label
            })
            return fields
          }, fields)
          return fields
        }, [{key: 'row', label: ''}])
      },
      perPageOptions() {
        let options = [
          {value: 5, text: 5},
          {value: 10, text: 10},
          {value: 25, text: 25},
          {value: 50, text: 50},
          {value: 100, text: 100},
        ]
        return options
      },
      columnToSchema() {
        return _.reduce(this.schema || [], (map, sch) => {
          return _.reduce(sch.columns, (map, column) => {
            map[column.name] = column
            return map
          }, map)
          return map
        }, [])
      },
      canImport() {
        return !!this.items && this.items.length > 0 && this.is_valid && !this.operation
      },
      areAllBackEndValidated() {
        return _.reduce(this.items || [], (areAllValid, item) => {
          if (!item.back_end_validated) areAllValid = false
          return areAllValid
        }, this.items.length > 0)
      }
    },
    methods: {
      onShown() {
        this.loadSchema()
        this.maybeFindConditionalQuestions()
      },
      onHidden() {
        this.schema = null
        this.reset()
      },
      reset() {
        this.busy = false
        this.error = null
        this.text = null
        this.header = null
        this.header_row = null
        this.header_r = null
        this.errors = []
        this.error_row = null
        this.error_r = null
        this.error_schema = null
        this.error_c = null
        this.is_valid = false
        this.items = null
        this.currentPage = 1
        this.operation = null
        this.operation_index = null
        this.pay_with = 'pay_later'
      },
      loadSchema() {
        this.busy = true
        this.error = null
        axios.get(this.schemaUrl)
        .then(response => {
          this.busy = false
          this.schema = response.data
        })
        .catch(error => {
          this.busy = false
          this.schema = null
          this.error = _.errorFromAjax(error)
        })
      },
      onPaste(e) {
        e.preventDefault()
        this.busy = true
        this.text = (e.clipboardData || window.clipboardData).getData("text")
        this.currentPage = 1
        setTimeout(() => {
          this.validate()
        }, 500)
        return false
      },
      validate() {
        this.errors = []
        this.error_row = null
        this.error_r = null
        this.error_schema = null
        this.error_c = null
        this.is_valid = false
        this.header_r = null
        this.header_row = null
        this.headers = _.map(this.fields, 'key')        
        this.operation = null
        this.operation_index = null
        this.busy = false
        this.error = null

        // Validate the header row
        let rows = (this.text || '').split('\n')
        if (rows.length == 0) {
          this.errors.push(trans('data_missing'))
          return
        }
        let csv = ''
        for (let r = 0; r < rows.length; r++) {
          let row = rows[r]
          let columns = row.split('\t')
          for (let c = 0; c < columns.length; c++) {
            let col = (columns[c] || '').trim()
            if (!!col && this.headers.indexOf(col) >= 0 && !this.header_row) {
              this.header_r = r
              this.header_row = row
              break
            }
          }
          if (this.header_r == r) {
            for (let c = 0; c < columns.length; c++) {
              let col = (columns[c] || '').trim()
              if (this.headers.indexOf(col) === -1) {
                this.errors.push(trans('data_invalid_header').replace(':column', col))
                this.error_row = row
                this.error_r = r
                this.error_c = col
                return
              }
            }
            csv = row + '\n'
          } else {
            csv += row + '\n'
          }
        }
        if (!this.header_row) {
          this.errors.push(trans('data_missing_header'))
          return
        }

        // Validate the data
        this.items = parseCsv(csv)
        if (this.items.length == 0) {
          this.errors.push(trans('valid_header_data_missing'))
          return
        }
        for (let i = 0; i < this.items.length; i++) {
          let item = this.items[i]
          if (!item) continue
          let keys = _.keys(item)
          item.row = this.header_r + i + 1
          item.back_end_validated = false
          item.imported = false
          let non_required_conditional_slugs = this.findNonReqConditionalSlugs(item)
          for (let k = 0; k < keys.length; k++) {
            let key = keys[k]
            if (!key) continue
            let schema = this.columnToSchema[key]
            let og_required = schema.required
            let value = item[key]
            if (_.isString(value)) value = value.trim()
            if (_.includes(non_required_conditional_slugs, _.trimEnd(schema.name, '.option'))) {
              // remove required if slug is included in non_req_condi... re-add with og_required when operation ends
              schema.required = false
            }
            if (!value && schema.required) {
              this.errors.push(trans('data_missing_required').replace(':column', schema.label).replace(':row', item.row + 1))
              this.error_row = rows[item.row]
              this.error_r = item.row
              this.error_schema = schema
              this.error_c = key
              return
            }
            else if (schema.type == 'input') {

            }
            else if (['select', 'radios', 'notify', 'age_category'].indexOf(schema.type) >= 0) {
              if (!schema.required && !(value+'').trim()) continue
              if (schema.attribute == 'gender') {
                let englishValues = _.map(schema.values, value => {
                  return _.translateGenderEN(value)
                })
                let frenchValues = _.map(schema.values, value => {
                  return _.translateGenderFR(value)
                })
                if (englishValues.indexOf(value) === -1 && frenchValues.indexOf(value) === -1 && schema.values.indexOf(value) === -1) {
                  this.errors.push(trans('data_invalid_select').replace(':column', schema.label).replace(':value', value).replace(':values', schema.values.join(', ')).replace(':row', item.row + 1))
                  this.error_row = rows[item.row]
                  this.error_r = item.row
                  this.error_schema = schema
                  this.error_c = key
                  return
                }
              }
              else {
                if (schema.values.indexOf(value) === -1) {
                  this.errors.push(trans('data_invalid_select').replace(':column', schema.label).replace(':value', value).replace(':values', schema.values.join(', ')).replace(':row', item.row + 1))
                  this.error_row = rows[item.row]
                  this.error_r = item.row
                  this.error_schema = schema
                  this.error_c = key
                  return
                }
              }
            }
            else if (schema.type == 'checkboxes') {
              let values = (value+'').split(',')
              for (let i = 0; i < values.length; i++) {
                let v = values[i].trim()
                if (!schema.required && !v.trim()) continue
                if (schema.values.indexOf(v) === -1) {
                  this.errors.push(trans('data_invalid_select').replace(':column', schema.label).replace(':value', v).replace(':values', schema.values.join(', ')).replace(':row', item.row + 1))
                  this.error_row = rows[item.row]
                  this.error_r = item.row
                  this.error_schema = schema
                  this.error_c = key
                  return
                }
              }
            }
            else if (schema.type == 'date_of_birth') {
              if (!schema.required && !(value+'').trim()) continue
              let m = moment(value, 'YYYY-MM-DD')
              if (!m.isValid() || m.format('YYYY-MM-DD') != value) {
                this.errors.push(trans('data_invalid_date_of_birth').replace(':column', schema.label).replace(':value', value).replace(':row', item.row + 1))
                this.error_row = rows[item.row]
                this.error_r = item.row
                this.error_schema = schema
                this.error_c = key
                return
              }
            }
            else if (schema.type == 'date') {
              if (!schema.required && !(value+'').trim()) continue
              let m = _.parseDate(value)
              if (!m.isValid()) {
                this.errors.push(trans('data_invalid_date').replace(':column', schema.label).replace(':value', value).replace(':row', item.row + 1))
                this.error_row = rows[item.row]
                this.error_r = item.row
                this.error_schema = schema
                this.error_c = key
                return
              }
            }
            else if (schema.type == 'time') {
              if (!schema.required && !(value+'').trim()) continue
              let m = _.parseTime(value)
              if (!m.isValid()) {
                this.errors.push(trans('data_invalid_time').replace(':column', schema.label).replace(':value', value).replace(':row', item.row + 1))
                this.error_row = rows[item.row]
                this.error_r = item.row
                this.error_schema = schema
                this.error_c = key
                return
              }
            }
            else if (schema.type == 'datetime') {
              let m = _.parseDate(value)
              if (!m.isValid()) {
                this.errors.push(trans('data_invalid_datetime').replace(':column', schema.label).replace(':value', value).replace(':row', item.row + 1))
                this.error_row = rows[item.row]
                this.error_r = item.row
                this.error_schema = schema
                this.error_c = key
                return
              }
            }
            else if (schema.type == 'number') {
              let error = null
              if (value && !_.isNumeric(value)) {
                error = trans('data_invalid_number').replace(':column', schema.label).replace(':value', value).replace(':row', item.row + 1)
              }
              if (!error && schema.minimum !== null & value < schema.minimum) {
                error = trans('data_invalid_number').replace(':column', schema.label).replace(':value', value).replace(':row', item.row + 1) +
                  ` ${trans('minimum')}: ${schema.minimum}.`
              }
              if (!error && schema.maximum !== null & value > schema.maximum) {
                error = trans('data_invalid_number').replace(':column', schema.label).replace(':value', value).replace(':row', item.row + 1) +
                  ` ${trans('maximum')}: ${schema.maximum}.`
              }
              // TODO: Validate schema.decimal_places
              if (!!error) {
                this.errors.push(error)
                this.error_row = rows[item.row]
                this.error_r = item.row
                this.error_schema = schema
                this.error_c = key
                return
              }
            }
            schema.required = og_required
          }
        }
        this.is_valid = true
      },
      serializeForm(item) { // where item is a row of values
        let non_required_conditional_slugs = this.findNonReqConditionalSlugs(item)
        return _.reduce(this.schema, (json, sch) => {
          if (_.includes(non_required_conditional_slugs, sch.slug) && !item[sch.slug]) {
            /* 
            if value is null and question is in non_req_conditio... remove from request,
            this allows to identify it in ImportFormRequest and make it nullable
            */
            return json
          }
          if (['input', 'number', 'select', 'radios', 'notify', 'age_category', 'admin', 'encrypted', 'sin', 'ssn', 'health_card'].indexOf(sch.type) >= 0) {
            json[sch.slug] = item[sch.slug]
          }
          else if (sch.type == 'age_category_multiple') {
            json[sch.slug] = { option: item[sch.slug+'.option'] }
          }
          else if (sch.type == 'date') {
            let v = item[sch.slug]
            json[sch.slug] = !!v ? [v] : null
          }
          else if (sch.type == 'checkboxes') {
            json[sch.slug] = (item[sch.slug]+'').split(',')
          }
          else if (sch.type == 'skills') {
            json[sch.slug] = (item[sch.slug]+'').split(',')
          }
          else if (sch.type == 'radios_branch') {
            if (sch.columns[0].type == 'radios') {
              json[sch.slug] = item[sch.slug]
            } else {
              json[sch.slug] = (item[sch.slug]+'').split(',')
            }
          }
          else if (['account_owner', 'person', 'child', 'other_parent', 'company'].indexOf(sch.type) >= 0) {
            json[sch.slug] = _.reduce(sch.columns, (person, col) => {
              let name = col.name
              let key = name.replace(new RegExp(`^${sch.slug}\.`), '')
              person[key] = item[name]
              if (key == 'gender') person[key] = _.serializeGender(item[name])
              if (key == 'relationship') person[key] = _.serializeRelationship(item[name])
              if (key == 'medical_conditions') person[key] = _.serializeMedicalConditions(item[name])
              if (key == 'allergies') person[key] = _.serializeAllergies(item[name])
              return person
            }, {})
          }
          else if (sch.type == 'contacts') {
            json[sch.slug] = buildContactsFromSchema(sch, item)
          }
          else if (sch.type == 'radio_invoice_items') {
            json[sch.slug] = {
              item: item[sch.slug+'.item'],
              addon: item[sch.slug+'.addon'],
            }
          }
          else if (sch.type == 'event_block') {
            let datesColumn = _.find(sch.columns, {attribute: 'dates'})
            let dates = ['Session', 'All', 'Tous'].indexOf(item[sch.slug+'.dates']) >= 0 ?
              // session, all dates
              datesColumn.dates :
              // drop-in
              _.map(item[sch.slug+'.dates'].split(','), v => {
                let m = _.parseDate(v)
                return m.isValid() ? m.toISOString() : null
              })
            dates = _.compact(dates)
            if (datesColumn.must_be_single_date) dates = dates[0]
            json[sch.slug] = {
              name: item[sch.slug+'.name'],
              dates: dates,
              price: item[sch.slug+'.price'],
              gl: item[sch.slug+'.gl'],
            }
            if (!!_.find(sch.columns, {attribute: 'first_name'})) {
              json[sch.slug].participants = [buildParticipantFromSchema(sch, item)]
            }
          }
          else if (sch.type == 'ticket_block') {
            let datesColumn = _.find(sch.columns, {attribute: 'dates'})
            let dates = ['Session', 'All', 'Tous'].indexOf(item[sch.slug+'.dates']) >= 0 ?
              // session, all dates
              datesColumn.dates :
              // drop-in
              _.map(item[sch.slug+'.dates'].split(','), v => {
                let m = _.parseDate(v)
                return m.isValid() ? m.toISOString() : null
              })
            dates = _.compact(dates)
            if (datesColumn.must_be_single_date) dates = dates[0]
            json[sch.slug] = {
              name: item[sch.slug+'.name'],
              dates: dates,
              price: item[sch.slug+'.price'],
              tickets: {
                quantity: item[sch.slug+'.tickets.quantity']
              },
            }
            if (!!_.find(sch.columns, {attribute: 'first_name'})) {
              json[sch.slug].participants = [buildParticipantFromSchema(sch, item)]
            }
          }
          else if (sch.type == 'events_block') {
            let name = item[sch.slug+'.name']
            let nameColumn = _.find(sch.columns, {attribute: 'name'})
            let am_class_id = nameColumn.ids[name]
            let datesColumn = _.find(sch.columns, {attribute: 'dates'})
            let dates = datesColumn.dates[name]
            let slug = `${sch.slug}_event_block_${am_class_id}`
            json[slug] = {
              am_class_id: am_class_id,
              question_id: sch.id,
              events_slug: sch.slug,
              dates: dates,
            }
            if (!!_.find(sch.columns, {attribute: 'first_name'})) {
              json[slug].participants = [buildParticipantFromSchema(sch, item)]
            }
          }
          else if (['variable_price', 'membership_block'].indexOf(sch.type) >= 0) {
            json[sch.slug] = { // slug.attr
              name: item[sch.slug+'.name'],
              price: item[sch.slug+'.price'],
              quantity: item[sch.slug+'.quantity'],
              gl: item[sch.slug+'.gl'],
              active_at: item[sch.slug+'.active_at'],
              expired_at: item[sch.slug+'.expired_at'],
            }
            if (!!_.find(sch.columns, {attribute: 'first_name'})) {
              json[sch.slug].participants = [buildParticipantFromSchema(sch, item)]
            }
          }
          else if (sch.type == 'products') {
            json[sch.slug] = _.values(_.reduce(sch.columns, (items, col) => {
              let parts = col.name.split('.') // slug.index.attr
              let index = parseInt(parts[1], 10)
              if (!items[index]) items[index] = {
                product_id: col.product_id,
                name: item[`${sch.slug}.${index}.name`],
                price: item[`${sch.slug}.${index}.price`],
                quantity: item[`${sch.slug}.${index}.quantity`],
                gl: item[`${sch.slug}.${index}.gl`],
              }
              return items
            }, {}))
          }
          return json
        }, {})
      },
      runOperationOnItemIndex(operation, index, onSuccess, onError) {
        let url = !!this.client && !!this.clientTab ?
          `/org/${this.organization.id}/client/tab/${this.clientTab.id}/form/${this.recipient_form.id}/` :
          `/organization/${this.organization.id}/form/${this.recipient_form.id}/`
        if (this.isPaymentForm && operation == 'import') {
          url += `import_payment_form/${this.pay_with}`
        } else {
          url += `${operation}_form`
        }
        this.operation = operation
        this.operation_index = index
        let item = this.items[index]
        if (!item) throw `Cannot find item at index: ${index}`
        return axios.post(url, this.serializeForm(item))
        .then(response => {
          index += 1
          if (index >= this.items.length) {
            onSuccess(response)
          } else {
            this.runOperationOnItemIndex(operation, index, onSuccess, onError)
          }
        })
        .catch(error => {
          onError(error)
        })
      },
      onImport(e) {
        e.preventDefault()

        this.busy = true
        this.error = null
        let operation = this.areAllBackEndValidated ? 'import' : 'validate'
        this.runOperationOnItemIndex(
          'validate',
          0,
          () => { // success
            this.busy = true
            this.runOperationOnItemIndex(
              'import',
              0,
              () => { // success
                this.countRecipients()
                  .catch(() => {}) // Do nothing
                  .then(this.onFinally)
              },
              error => { // error
                this.busy = false
                this.error = _.errorFromAjax(error)
              }
            )
          },
          error => { // error
            this.busy = false
            this.error = _.errorFromAjax(error)
          }
        )
      },
      onFinally() {
        this.busy = false
        this.operation = null
        this.operation_index = null
        this.$bvModal.hide('form-import-modal')
        this.$emit('imported')
      },
      countRecipients() {
        this.busy = true
        this.error = null
        let url = !!this.client && !!this.clientTab ?
          `/org/${this.organization.id}/client/tab/${this.clientTab.id}/form/${this.recipient_form.id}/count_recipients` :
          `/organization/${this.organization.id}/list/${this.recipient_form.id}/count_recipients`
        return axios.post(url)
        .then(response => {
          this.busy = false
        })
        .catch(error => {
          this.busy = false
          this.error = _.errorFromAjax(error)
        })
      },
      fetchQuestionsAndMap() {
        this.busy = true
        this.error = null
        let url = `/org/${this.organization.id}/form/${this.recipient_form.form_id}/questions_light`
        return axios.get(url)
        .then(response => {
          this.busy = false
          this.mapConditionals(response.data)
        })
        .catch(error => {
          this.busy = false
          this.error = _.errorFromAjax(error)
        })
      },
      maybeFindConditionalQuestions() {
        let questions = []
        if (this.recipient_form?.new_form?.form_questions) {
          questions = this.recipient_form?.new_form?.form_questions || []
          this.mapConditionals(questions)
        }
        else if (this.client && this.clientTab) {
          this.fetchQuestionsAndMap() // in client tab, questions are not loaded by default
        }
        else return
      },
      findNonReqConditionalSlugs(item) {
        /* 
        returns all non required questions for this item based on the conditional questions values
        */
        const item_conditionals = _.uniq(
          _.flatMap(item, (value, slug) => _.get(this.conditionals, [slug, value], []))
        )
        const all_conditionals = _.uniq(_.flatMap(this.conditionals, (value) => {
          return _.flatMap(value, v => v)
        }))
        return _.difference(all_conditionals, item_conditionals)
      },
      mapConditionals(questions) {
        /*
        populates this.conditionals, keyed by slug and value that identifies which question slugs are included.
        ex: this.conditionals['radios_branch0']['option1'] = ['inputs0', inputs1]
        */
        if (!_.isArray(questions) || questions.length == 0) return
        _.each(questions, question => {
          if (question.type == 'radios_branch') {
            if (!this.conditionals[question.slug]) {
              this.conditionals[question.slug] = {}
            }
            _.each(question.options.values, (value, index) => {
              let target_slugs = questions
                .filter(q => question.options?.show_question_ids?.[index]?.includes(q.id))
                .map(q => q.slug)
              this.conditionals[question.slug][value] = [...(this.conditionals[question.slug][value] || []), ...target_slugs]
            })
          }
        })
      }
    },
    mounted() {
      window.adminFormImportModal = this
    },
  }
</script>